import React from 'react'
import { useNavigate } from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import {
    changeDateFormat,
    onSetQuoteDetailsBreadcrumb
} from '../../helpers/Util'
import { addMixpanelTrackingEvent } from '../../services/CommonService'

const STATUS_CONFIG = {
    RUNNING: { label: 'Running', className: 'order-status-badge running' },
    PENDING: { label: 'Pending', className: 'order-status-badge pending' },
    COMPLETED: {
        label: 'Completed',
        className: 'order-status-badge completed'
    },
    CANCELLED: { label: 'Cancelled', className: 'order-status-badge cancelled' }
}

const getItemStatus = (item, activeTab) => {
    if (activeTab && activeTab !== 'ALL') return activeTab
    const s = item?.status?.toUpperCase()
    if (!s) return 'RUNNING'
    if (s === 'COMPLETED' || s === 'ARCHIVE') return 'COMPLETED'
    if (s === 'RUNNING') return 'RUNNING'
    if (s === 'PENDING') return 'PENDING'
    if (s === 'INITIALIZED') {
        if (item?.quoteApprovalStatus === 'CANCELLED') return 'CANCELLED'
        return 'PENDING'
    }
    return 'RUNNING'
}

// Mirrors getStatusText logic from Util.js getQuoteStatusColor
const getCostingStatusText = (status) => {
    const statusMap = {
        OFFER_SENT: 'Quote Received',
        REQUEST_FOR_REVISION: 'Re-cost',
        APPROVED: 'Approved',
        CANCELLED: 'Cancelled',
        REJECTED: 'Rejected',
        QUOTED: 'Costing Done',
        FACTORY_COSTING: 'Factory Costing'
    }
    return statusMap[status] || 'Requested'
}

const getCostingActivityStatus = (status) => {
    switch (status) {
        case 'INITIALIZED':
            return 'purple-1'
        case 'QUOTED':
            return 'blue-1'
        case 'OFFER_SENT':
            return 'yellow-1'
        case 'APPROVED':
            return 'green-2'
        case 'REJECTED':
        case 'REQUEST_FOR_REVISION':
            return 'red-2'
        case 'FACTORY_COSTING':
            return 'orange-1'
        case 'ARCHIVE':
        case 'CANCELLED':
            return 'red-3'
        default:
            return ''
    }
}

/**
 * Returns all entries from statusCountList that have count > 0
 * as an array of { label, statusCode, count }.
 * Falls back to a single entry from costingStatus if list is absent.
 */
const getCostingStages = (q) => {
    const list = q?.statusCountList
    if (Array.isArray(list) && list.length > 0) {
        const active = list.filter((act) => act.count > 0)
        if (active.length > 0) {
            return active.map((act) => ({
                label: getCostingStatusText(act.status),
                statusCode: act.status || null,
                count: act.count
            }))
        }
        // nothing has count — show first entry with no count
        const first = list[0]
        return [
            {
                label: getCostingStatusText(first?.status),
                statusCode: first?.status || null,
                count: 0
            }
        ]
    }
    const statusCode = q?.costingStatus || null
    return [
        {
            label: statusCode ? getCostingStatusText(statusCode) : '—',
            statusCode,
            count: 0
        }
    ]
}

const QuoteTableView = ({ quoteStore, activeTab }) => {
    const navigate = useNavigate()
    const quotes = quoteStore?.quotes?.data ?? []

    const handleRoute = (q) => {
        onSetQuoteDetailsBreadcrumb(q, activeTab, (url) => {
            addMixpanelTrackingEvent({}, 'BUYER_QUOTE_ON_CLICK')
            navigate(url)
        })
    }

    if (!quotes.length) return null

    return (
        <div className='order-table-view mt-[14px]'>
            <div className='overflow-x-auto'>
                <table className='table order-list-table w-full'>
                    <thead>
                        <tr>
                            <th>Ref</th>
                            <th>Style</th>
                            <th className='text-right'>Designs</th>
                            <th>Quote Status</th>
                            <th>Expected By</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {quotes.map((q, index) => {
                            const status = getItemStatus(q, activeTab)
                            const statusCfg =
                                STATUS_CONFIG[status] ||
                                STATUS_CONFIG['RUNNING']
                            const costingStages = getCostingStages(q)
                            const expectedDate =
                                q?.estimatedOrderDeliveryDate || q?.requiredDate
                            return (
                                <tr
                                    key={`quote_table_${q.id || index}`}
                                    className='cursor-pointer'
                                    onClick={() => handleRoute(q)}
                                >
                                    <td>
                                        <span className='regular-14 weight-500 black'>
                                            {q.referenceNumber || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <Tooltip
                                            title={q.title || ''}
                                            placement='top'
                                            arrow
                                        >
                                            <span className='regular-14 black'>
                                                {q.title?.length > 40
                                                    ? `${q.title.slice(0, 40)}…`
                                                    : q.title || '—'}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className='text-right'>
                                        <span className='regular-14 black'>
                                            {q.noOfDesign ?? '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className='flex flex-wrap gap-1'>
                                            {costingStages.map((stage) => (
                                                <span
                                                    key={stage.statusCode}
                                                    className={`status ${getCostingActivityStatus(
                                                        stage.statusCode
                                                    )}`}
                                                >
                                                    {stage.label}
                                                    {stage.count > 0 && (
                                                        <span className='badge-count ml-1'>
                                                            ({stage.count})
                                                        </span>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <span className='regular-14 gray_300'>
                                            {expectedDate
                                                ? changeDateFormat(
                                                      expectedDate,
                                                      'YYYY-MM-DD',
                                                      'DD MMM, YYYY'
                                                  )
                                                : '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={statusCfg.className}>
                                            {statusCfg.label}
                                        </span>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default QuoteTableView
